import { useState, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/projects/Civi_Circle_Admin/components/ui/card";
import { Button } from "@/projects/Civi_Circle_Admin/components/ui/button";
import { Badge } from "@/projects/Civi_Circle_Admin/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/Civi_Circle_Admin/components/ui/table";
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Play,
  RefreshCw,
  Settings,
  Trash,
  Download,
  Search,
  Users,
  UserX,
  Briefcase,
  FileSpreadsheet,
  Upload,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/projects/Civi_Circle_Admin/components/ui/dialog";
import { Input } from "@/projects/Civi_Circle_Admin/components/ui/input";
import { Checkbox } from "@/projects/Civi_Circle_Admin/components/ui/checkbox";
import { toast } from "sonner";
import {
  Typography,
  Upload as AntUpload,
  Progress,
  Space,
  Spin,
  Alert,
  Form,
  Select,
  Popconfirm,
} from "antd";
import { format } from "date-fns";
import { notify } from "@/components/common/Notification";
import {
  DataOperationsService,
  UploadService,
  SchemaService,
  ImportManagementService,
} from "@/services/APIs";
import CityCollaborationService from "@/services/APIs/CityCollaborationService";
import { ColumnMapping } from "@/utils/types/dataOperator";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { filterByLabel } from "@/lib/filterByLabel";
// @ts-ignore - xlsx types may not be available
import * as XLSX from "xlsx";
import * as ExcelJS from "exceljs";
import { axiosConfig } from "@/services/axiosConfig";
const { Text } = Typography;

interface RosterUpload {
  id: string;
  file_name: string;
  status: string;
  total_records: number;
  new_users: number;
  updated_users: number;
  deactivated_users: number;
  errors: unknown[];
  created_at: string;
  processed_at: string | null;
}

const statusColors: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  processing: "outline",
  completed: "default",
  failed: "destructive",
};

const statusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="h-3 w-3" />,
  processing: <Clock className="h-3 w-3 animate-spin" />,
  completed: <CheckCircle className="h-3 w-3" />,
  failed: <XCircle className="h-3 w-3" />,
};

// const mockUploads: RosterUpload[] = [
//   { id: "1", file_name: "Q1_2024_roster.xlsx", status: "completed", total_records: 245, new_users: 12, updated_users: 28, deactivated_users: 3, errors: [], created_at: "2024-01-15T10:30:00Z", processed_at: "2024-01-15T10:35:00Z" },
// ];

const SCHEMA_NAME = "MM Member";
const ENTITY_NAME = "mm_member";

const ROSTER_TEMPLATE_HEADERS = [
  "Prefix",
  "Department",
  "Position",
  "First Name",
  "Middle Name",
  "Last Name",
  "Phone Number",
  "Gender",
  "Suffix",
  "Date of Birth",
  "Description",
  "Available for Contract",
  "Visible in Marketplace",
  "Email",
  "Post-Retirement Opportunities",
  "Est. Retirement Date",
  "Seniority Level",
  "Open to Peer Consultation",
] as const;

const REQUIRED_TEMPLATE_HEADERS = new Set<
  (typeof ROSTER_TEMPLATE_HEADERS)[number]
>(["Department", "Position", "Email"]);

const CIVI_CIRCLE_API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, "") ??
  "https://api.civicircle.com/api";

const DROPDOWN_ENDPOINTS = {
  department: `${CIVI_CIRCLE_API_BASE_URL}/dropdown/department/`,
  position: `${CIVI_CIRCLE_API_BASE_URL}/dropdown/position/`,
  suffix: `${CIVI_CIRCLE_API_BASE_URL}/dropdown/suffix/`,
  prefix: `${CIVI_CIRCLE_API_BASE_URL}/formily/dynamic-model/prefix_child/grid_data/?page=1&page_size=10`,
} as const;

const GENDER_OPTIONS = ["Male", "Female", "Other"] as const;
const YES_NO_OPTIONS = ["Yes", "No"] as const;
const POST_RETIREMENT_OPPORTUNITIES_OPTIONS = [
  "Advisory Role",
  "Contract Work",
  "Peer Mentoring",
] as const;
const SENIORITY_LEVEL_OPTIONS = [
  "Director",
  "Manager",
  "Supervisor",
  "Staff",
  "Intern",
] as const;

/** Convert a 1-based column index to Excel letter(s): 1→A, 2→B, 27→AA … */
function colLetter(n: number): string {
  let result = "";
  let num = n;
  while (num > 0) {
    const rem = (num - 1) % 26;
    result = String.fromCharCode(65 + rem) + result;
    num = Math.floor((num - 1) / 26);
  }
  return result;
}

async function fetchDropdownLabels(
  url: string,
  labelKey: string = "label"
): Promise<string[]> {
  const res = await axiosConfig.get(url);
  const data = (res as any)?.data ?? res;

  // Handle { grid_data: [...] } or direct array
  const raw: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.grid_data)
      ? data.grid_data
      : [];

  const labels = raw
    .filter(o => o && (o.is_active ?? true))
    .map(o => {
      const val = o[labelKey] ?? o.label ?? o.name;
      return typeof val === "string" ? val.trim() : "";
    })
    .filter(Boolean);

  return Array.from(new Set(labels));
}

export interface ImportedMember {
  name: string;
  email: string;
  department: string;
  role: string;
  organization: string;
  phone: string;
  status: "active" | "retired" | "job_changed" | "new" | "unchanged";
  previousRole?: string;
  previousDepartment?: string;
  retiredDate?: string;
  newOrganization?: string;
  [key: string]: unknown;
}

interface ImportPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: ImportedMember[];
  fileName: string;
  onConfirmImport: (members: ImportedMember[]) => void;
}

export function ImportPreviewDialog({
  open,
  onOpenChange,
  members,
  fileName,
  onConfirmImport,
}: ImportPreviewDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [memberStatuses, setMemberStatuses] = useState<
    Record<number, "retired" | "job_changed">
  >({});
  const [selectedMembers, setSelectedMembers] = useState<Set<number>>(
    new Set()
  );
  const [selectAll, setSelectAll] = useState(false);

  const filteredMembers = members.filter(member => {
    const matchesSearch =
      (member.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.department || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (member.organization || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const retiredCount = Object.values(memberStatuses).filter(
    s => s === "retired"
  ).length;
  const changedCount = Object.values(memberStatuses).filter(
    s => s === "job_changed"
  ).length;
  const unassignedCount = members.length - Object.keys(memberStatuses).length;

  const handleStatusChange = (
    index: number,
    status: "retired" | "job_changed"
  ) => {
    setMemberStatuses(prev => {
      const next = { ...prev };
      if (next[index] === status) {
        delete next[index];
      } else {
        next[index] = status;
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allIndices = new Set(members.map((_, i) => i));
      setSelectedMembers(allIndices);
    } else {
      setSelectedMembers(new Set());
    }
  };

  const handleSelectMember = (index: number, checked: boolean) => {
    setSelectedMembers(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
    if (!checked) {
      setSelectAll(false);
    }
  };

  const handleBulkAssign = (status: "retired" | "job_changed") => {
    if (selectedMembers.size === 0) {
      toast.error("Please select members first");
      return;
    }
    setMemberStatuses(prev => {
      const next = { ...prev };
      selectedMembers.forEach(i => {
        next[i] = status;
      });
      return next;
    });
    toast.success(
      `${selectedMembers.size} members marked as ${status === "retired" ? "Retired" : "Change Member"}`
    );
  };

  const handleConfirm = () => {
    if (Object.keys(memberStatuses).length === 0) {
      toast.error("Please assign status to at least one member");
      return;
    }
    const updatedMembers = members.map((m, i) => ({
      ...m,
      status: memberStatuses[i] || m.status,
    }));
    onConfirmImport(updatedMembers);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-[95vw] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Preview — {fileName}
          </DialogTitle>
          <DialogDescription>
            Assign each member as Retired or Change Member. Total{" "}
            {members.length} records found.
          </DialogDescription>
        </DialogHeader>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border p-3 text-center">
            <div className="mb-1 flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <UserX className="h-3 w-3 text-destructive" /> Retired
            </div>
            <span className="text-xl font-bold text-destructive">
              {retiredCount}
            </span>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="mb-1 flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <Briefcase className="text-warning h-3 w-3" /> Change Member
            </div>
            <span className="text-warning text-xl font-bold">
              {changedCount}
            </span>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="mb-1 flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <Users className="h-3 w-3 text-muted-foreground" /> Unassigned
            </div>
            <span className="text-xl font-bold text-muted-foreground">
              {unassignedCount}
            </span>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {selectedMembers.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive/10"
              onClick={() => handleBulkAssign("retired")}
              disabled={selectedMembers.size === 0}
            >
              <UserX className="mr-1 h-3.5 w-3.5" />
              Mark Retired
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-warning text-warning hover:bg-warning/10"
              onClick={() => handleBulkAssign("job_changed")}
              disabled={selectedMembers.size === 0}
            >
              <Briefcase className="mr-1 h-3.5 w-3.5" />
              Mark Change
            </Button>
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, department..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        <div className="min-h-0 flex-1 overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky top-0 w-[40px] bg-background">
                  <Checkbox
                    checked={selectAll}
                    onCheckedChange={checked => handleSelectAll(!!checked)}
                  />
                </TableHead>
                <TableHead className="sticky top-0 bg-background">#</TableHead>
                <TableHead className="sticky top-0 bg-background">
                  Name
                </TableHead>
                <TableHead className="sticky top-0 bg-background">
                  Email
                </TableHead>
                <TableHead className="sticky top-0 bg-background">
                  Organization
                </TableHead>
                <TableHead className="sticky top-0 bg-background">
                  Department
                </TableHead>
                <TableHead className="sticky top-0 bg-background">
                  Role
                </TableHead>
                <TableHead className="sticky top-0 bg-background">
                  Phone
                </TableHead>
                <TableHead className="sticky top-0 min-w-[180px] bg-background">
                  Assign Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredMembers.map(member => {
                  const realIndex = members.indexOf(member);
                  const assignedStatus = memberStatuses[realIndex];
                  return (
                    <TableRow
                      key={realIndex}
                      className={
                        assignedStatus === "retired"
                          ? "bg-destructive/5"
                          : assignedStatus === "job_changed"
                            ? "bg-warning/5"
                            : ""
                      }
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedMembers.has(realIndex)}
                          onCheckedChange={checked =>
                            handleSelectMember(realIndex, !!checked)
                          }
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {realIndex + 1}
                      </TableCell>
                      <TableCell className="font-medium">
                        {member.name || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {member.email || "—"}
                      </TableCell>
                      <TableCell>{member.organization || "—"}</TableCell>
                      <TableCell>{member.department || "—"}</TableCell>
                      <TableCell>{member.role || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {member.phone || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant={
                              assignedStatus === "retired"
                                ? "default"
                                : "outline"
                            }
                            className={
                              assignedStatus === "retired"
                                ? "h-7 bg-destructive px-2 text-xs text-destructive-foreground hover:bg-destructive/90"
                                : "h-7 border-destructive/30 px-2 text-xs text-destructive hover:bg-destructive/10"
                            }
                            onClick={() =>
                              handleStatusChange(realIndex, "retired")
                            }
                          >
                            <UserX className="mr-1 h-3 w-3" />
                            Retired
                          </Button>
                          <Button
                            size="sm"
                            variant={
                              assignedStatus === "job_changed"
                                ? "default"
                                : "outline"
                            }
                            className={
                              assignedStatus === "job_changed"
                                ? "bg-warning text-warning-foreground hover:bg-warning/90 h-7 px-2 text-xs"
                                : "border-warning/30 text-warning hover:bg-warning/10 h-7 px-2 text-xs"
                            }
                            onClick={() =>
                              handleStatusChange(realIndex, "job_changed")
                            }
                          >
                            <Briefcase className="mr-1 h-3 w-3" />
                            Change
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <p className="text-xs text-muted-foreground">
          Showing {filteredMembers.length} of {members.length} records •{" "}
          {retiredCount} Retired • {changedCount} Changed • {unassignedCount}{" "}
          Unassigned
        </p>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            <Upload className="mr-2 h-4 w-4" />
            Confirm Import ({Object.keys(memberStatuses).length} assigned)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RosterManagementTab() {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<RosterUpload[]>([]);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [importId, setImportId] = useState<string | null>(null);
  const [fileColumns, setFileColumns] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdatingMapping, setIsUpdatingMapping] = useState(false);
  const [isRunningImport, setIsRunningImport] = useState(false);
  const [isMappingSaved, setIsMappingSaved] = useState(false);
  const [mappingError, setMappingError] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewMembers, setPreviewMembers] = useState<ImportedMember[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [form] = Form.useForm();

  const { data: columnGridData, isLoading: isLoadingColumns } = useQuery({
    queryKey: ["columnGridData", SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(SCHEMA_NAME),
    enabled: currentStep >= 2,
  });

  // Fetch upload history from API
  const { data: importListData } = useQuery({
    queryKey: ["imports", "list", SCHEMA_NAME, "city"],
    queryFn: () =>
      ImportManagementService.getImportsBySchema(SCHEMA_NAME, 1, 100, true),
    // refetchInterval: 5000, // Refetch every 5 seconds to get updated status
  });

  const dbColumns =
    columnGridData?.grid
      ?.filter((field: any) => {
        const systemFields = [
          "id",
          "is_active",
          "created_on",
          "updated_on",
          "created_by",
          "updated_by",
          "tenant",
          "created_by_id",
          "updated_by_id",
          "tenant_id",
        ];
        return !systemFields.includes(field.field_name.toLowerCase());
      })
      ?.map((field: any) => ({
        label: field.field_title,
        value: field.field_name.toLowerCase(),
      })) ?? [];

  const getMappableFileColumns = () => {
    const systemFields = [
      "id",
      "is_active",
      "created_on",
      "updated_on",
      "created_by",
      "updated_by",
      "tenant",
      "created_by_id",
      "updated_by_id",
      "tenant_id",
    ];
    return fileColumns.filter(
      column => !systemFields.includes(column.toLowerCase())
    );
  };

  const handleSetupImport = async (schema: string, documentId: string) => {
    const importResponse = await DataOperationsService.importData(schema, {
      document_id: documentId,
      import_type: "create new records",
    });

    if (importResponse?.import_id) {
      return importResponse.import_id;
    } else {
      notify.error("Import Setup Failed", "No import ID received from server.");
      return null;
    }
  };

  const handleFileUpload = useCallback(async (file: File) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    // Validate file extension
    if (!fileExtension || !["csv", "xlsx", "xls"].includes(fileExtension)) {
      notify.error(
        "Unsupported File Type",
        "Please upload a CSV or Excel file (.csv, .xlsx, .xls)"
      );
      return;
    }

    // Read file headers based on file type
    const readFileHeaders = (): Promise<string[]> => {
      return new Promise((resolve, reject) => {
        if (fileExtension === "csv") {
          const reader = new FileReader();
          reader.onload = e => {
            try {
              const text = e.target?.result as string;
              const lines = text.split("\n");
              if (lines.length > 0) {
                const headers = lines[0]
                  .split(",")
                  .map(h => h.trim().replace(/"/g, ""));
                resolve(headers);
              } else {
                reject(new Error("CSV file is empty"));
              }
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = () => reject(new Error("Failed to read CSV file"));
          reader.readAsText(file);
        } else if (fileExtension === "xlsx" || fileExtension === "xls") {
          const reader = new FileReader();
          reader.onload = e => {
            try {
              const data = e.target?.result;
              const workbook = XLSX.read(data, { type: "binary" });
              // Get the first sheet
              const firstSheetName = workbook.SheetNames[0];
              if (!firstSheetName) {
                reject(new Error("Excel file has no sheets"));
                return;
              }
              const worksheet = workbook.Sheets[firstSheetName];
              // Convert to JSON to get headers (first row)
              const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
              });
              if (jsonData.length > 0 && Array.isArray(jsonData[0])) {
                const headers = (jsonData[0] as string[]).map(h =>
                  String(h).trim()
                );
                resolve(headers);
              } else {
                reject(new Error("Excel file is empty"));
              }
            } catch (_error) {
              reject(
                new Error(
                  "Failed to read Excel file. Please check the file format."
                )
              );
            }
          };
          reader.onerror = () => reject(new Error("Failed to read Excel file"));
          reader.readAsBinaryString(file);
        } else {
          reject(new Error("Unsupported file type"));
        }
      });
    };

    // Upload file
    setIsUploading(true);
    try {
      // Read headers first
      const headers = await readFileHeaders();
      setFileColumns(headers);
      setUploadedFile(file);

      const response = await UploadService.uploadFiles({
        folderName: "importfiles",
        files: [file],
      });

      const docId = response?.[0]?.id;
      if (!docId) {
        notify.error("Upload Failed", "No document ID received from server.");
        setIsUploading(false);
        return;
      }

      // Setup import using service
      const importId = await handleSetupImport(ENTITY_NAME, docId);
      if (importId) {
        setImportId(importId);
        setCurrentStep(2);
      }
    } catch (error: any) {
      notify.error(
        "File Upload Failed",
        error?.message ?? "Failed to upload file. Please check the file format."
      );
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleUpdateMapping = async (
    importIdValue: string,
    mapping: ColumnMapping[]
  ): Promise<boolean> => {
    setIsUpdatingMapping(true);
    try {
      const response = await DataOperationsService.updateColumnMapping(
        importIdValue,
        { column_mapping: mapping }
      );
      if (response) {
        setIsMappingSaved(true);
        return true;
      }
      return false;
    } catch (error: any) {
      notify.error(
        "Mapping Failed",
        error?.message ?? "Failed to save column mapping."
      );
      return false;
    } finally {
      setIsUpdatingMapping(false);
    }
  };

  const handleColumnMapping = async (values: any): Promise<boolean> => {
    setMappingError("");

    // Filter out empty values and create mapping
    const mapping: ColumnMapping[] = Object.entries(values)
      .filter(([, value]) => value && value !== "")
      .map(([key, value]) => ({
        file_column_name: value as string,
        db_column_name: key,
      }));

    if (mapping.length === 0) {
      setMappingError("Please map at least one column before proceeding.");
      notify.warning(
        "No Mappings",
        "Please map at least one column before proceeding."
      );
      return false;
    }

    // Check for duplicate file_column_name
    const fileNames = mapping.map(m => m.file_column_name);
    const hasDuplicates = fileNames.length !== new Set(fileNames).size;
    if (hasDuplicates) {
      setMappingError(
        "Each file column can only be mapped to one database field."
      );
      notify.error(
        "Duplicate Mapping",
        "Each file column can only be mapped once."
      );
      return false;
    }

    if (importId) {
      const saved = await handleUpdateMapping(importId, mapping);
      return saved;
    } else {
      setMappingError("No import ID available. Please upload a file first.");
      notify.error("No Import ID", "Please upload a file first.");
      return false;
    }
  };

  const handleResetMapping = () => {
    form.resetFields();
    setMappingError("");
    setIsMappingSaved(false);
    notify.success("Mapping Reset", "Column mapping has been reset.");
  };

  const handleDownloadExcelFormat = async () => {
    setIsDownloading(true);
    try {
      const [prefixes, departments, positions, suffixes] = await Promise.all([
        fetchDropdownLabels(DROPDOWN_ENDPOINTS.prefix, "prefix"),
        fetchDropdownLabels(DROPDOWN_ENDPOINTS.department),
        fetchDropdownLabels(DROPDOWN_ENDPOINTS.position),
        fetchDropdownLabels(DROPDOWN_ENDPOINTS.suffix),
      ]);

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Roster");

      // Hidden sheet to hold dropdown values
      const listsSheet = wb.addWorksheet("_Lists");
      listsSheet.state = "veryHidden";

      const putList = (colIndex: number, values: string[]) => {
        values.forEach((v, r) => {
          listsSheet.getCell(r + 1, colIndex).value = v;
        });
      };

      // _Lists columns:
      // A=Prefix, B=Department, C=Position, D=Suffix, E=Gender,
      // F=Yes/No, G=Post-Retirement Opportunities, H=Seniority Level
      putList(1, prefixes);
      putList(2, departments);
      putList(3, positions);
      putList(4, suffixes);
      putList(5, [...GENDER_OPTIONS]);
      putList(6, [...YES_NO_OPTIONS]);
      putList(7, [...POST_RETIREMENT_OPPORTUNITIES_OPTIONS]);
      putList(8, [...SENIORITY_LEVEL_OPTIONS]);

      ws.columns = ROSTER_TEMPLATE_HEADERS.map(h => ({
        header: REQUIRED_TEMPLATE_HEADERS.has(h) ? `${h} *` : h,
        key: h,
        width: Math.max(18, h.length + 4),
      }));

      // Header styling
      const headerRow = ws.getRow(1);
      headerRow.height = 20;
      ROSTER_TEMPLATE_HEADERS.forEach((h, idx) => {
        const cell = headerRow.getCell(idx + 1);
        const isRequired = REQUIRED_TEMPLATE_HEADERS.has(h);
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: isRequired ? "FFCC0000" : "FF1F3864" }, // required red, otherwise dark blue
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          bottom: { style: "thin", color: { argb: "FFAAAAAA" } },
        };
      });

      // Add one empty sample row so validations are visible immediately
      ws.addRow(Array(ROSTER_TEMPLATE_HEADERS.length).fill(""));

      // Data validation ranges (rows 2..1001)
      const DATA_ROWS = 1000;
      const applyListValidation = (
        headerName: (typeof ROSTER_TEMPLATE_HEADERS)[number],
        listColIndex: number,
        listLen: number
      ) => {
        if (listLen <= 0) {
          return;
        }
        const templateColIndex =
          ROSTER_TEMPLATE_HEADERS.indexOf(headerName) + 1; // 1-based
        if (templateColIndex <= 0) {
          return;
        }
        const listLetter = colLetter(listColIndex);
        const formula = `'_Lists'!$${listLetter}$1:$${listLetter}$${listLen}`;
        const allowBlank = !REQUIRED_TEMPLATE_HEADERS.has(headerName);
        for (let row = 2; row <= DATA_ROWS + 1; row++) {
          ws.getCell(row, templateColIndex).dataValidation = {
            type: "list",
            allowBlank,
            formulae: [formula],
            showErrorMessage: true,
            errorStyle: "warning",
            errorTitle: "Invalid value",
            error: allowBlank
              ? "Please select a value from the dropdown list."
              : "This field is required. Please select a value from the dropdown list.",
          };
        }
      };

      applyListValidation("Prefix", 1, prefixes.length);
      applyListValidation("Department", 2, departments.length);
      applyListValidation("Position", 3, positions.length);
      applyListValidation("Suffix", 4, suffixes.length);
      applyListValidation("Gender", 5, GENDER_OPTIONS.length);
      applyListValidation(
        "Open to Peer Consultation",
        6,
        YES_NO_OPTIONS.length
      );
      applyListValidation("Available for Contract", 6, YES_NO_OPTIONS.length);
      applyListValidation("Visible in Marketplace", 6, YES_NO_OPTIONS.length);
      applyListValidation(
        "Post-Retirement Opportunities",
        7,
        POST_RETIREMENT_OPPORTUNITIES_OPTIONS.length
      );
      applyListValidation("Seniority Level", 8, SENIORITY_LEVEL_OPTIONS.length);

      // Date of Birth: enforce date input + date formatting
      const dobColIndex = ROSTER_TEMPLATE_HEADERS.indexOf("Date of Birth") + 1; // 1-based
      if (dobColIndex > 0) {
        ws.getColumn(dobColIndex).numFmt = "dd-mm-yyyy";
        for (let row = 2; row <= DATA_ROWS + 1; row++) {
          ws.getCell(row, dobColIndex).dataValidation = {
            type: "date",
            operator: "between",
            allowBlank: true,
            formulae: [new Date(1900, 0, 1), new Date(2100, 11, 31)],
            showErrorMessage: true,
            errorStyle: "warning",
            errorTitle: "Invalid date",
            error: "Please enter a valid date (dd-mm-yyyy).",
            showInputMessage: true,
            promptTitle: "Date of Birth",
            prompt: "Enter/select a date (dd-mm-yyyy).",
          };
        }
      }

      // Est. Retirement Date: enforce date input + date formatting
      const retirementColIndex =
        ROSTER_TEMPLATE_HEADERS.indexOf("Est. Retirement Date") + 1; // 1-based
      if (retirementColIndex > 0) {
        ws.getColumn(retirementColIndex).numFmt = "dd-mm-yyyy";
        for (let row = 2; row <= DATA_ROWS + 1; row++) {
          ws.getCell(row, retirementColIndex).dataValidation = {
            type: "date",
            operator: "between",
            allowBlank: true,
            formulae: [new Date(1900, 0, 1), new Date(2100, 11, 31)],
            showErrorMessage: true,
            errorStyle: "warning",
            errorTitle: "Invalid date",
            error: "Please enter a valid date (dd-mm-yyyy).",
            showInputMessage: true,
            promptTitle: "Est. Retirement Date",
            prompt: "Enter/select a date (dd-mm-yyyy).",
          };
        }
      }

      // Download
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Roster_Bulk_Upload_Template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      notify.success("Downloaded", "Excel template downloaded successfully.");
    } catch (error: any) {
      notify.error(
        "Download Failed",
        error?.message ?? "Failed to generate/download Excel template."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRunImport = async () => {
    if (!importId) {
      notify.error(
        "No Import ID",
        "Please upload a file and save mapping first."
      );
      return;
    }

    setIsRunningImport(true);
    try {
      const response = (await DataOperationsService.runImport(importId, {
        is_city_member: true,
      })) as any;

      // If success is false, show missing members popup
      if (response?.success === false) {
        const missingMembers: ImportedMember[] = (
          response?.missing_members ?? []
        ).map((m: any) => ({
          id: m.id,
          name:
            m.name ??
            `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() ??
            "—",
          email: m.email ?? "—",
          department: m.department ?? "—",
          role: m.role ?? m.position ?? "—",
          organization: m.organization ?? "—",
          phone: m.phone ?? m.phonenumber ?? "—",
          status: "active" as const,
        }));
        setPreviewMembers(missingMembers);
        setIsPreviewOpen(true);
        return;
      }

      // success: true — normal flow
      notify.success("Import Started", "Check history for status.");
      queryClient.invalidateQueries({
        queryKey: ["imports", "list", SCHEMA_NAME, "city"],
      });
      setCurrentStep(1);
      setImportId(null);
      setFileColumns([]);
      form.resetFields();
      setIsMappingSaved(false);
    } catch (error: any) {
      // Also handle missing members if API returns 4xx with success:false
      const errData = error?.response?.data;
      if (errData?.success === false && errData?.missing_members?.length > 0) {
        const missingMembers: ImportedMember[] = errData.missing_members.map(
          (m: any) => ({
            id: m.id,
            name:
              m.name ??
              `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() ??
              "—",
            email: m.email ?? "—",
            department: m.department ?? "—",
            role: m.role ?? m.position ?? "—",
            organization: m.organization ?? "—",
            phone: m.phone ?? m.phonenumber ?? "—",
            status: "active" as const,
          })
        );
        setPreviewMembers(missingMembers);
        setIsPreviewOpen(true);
        return;
      }
      notify.error(
        "Import Failed",
        error?.message ?? "Failed to start import process."
      );
    } finally {
      setIsRunningImport(false);
    }
  };

  // Handle configure mapping for pending imports
  const handleConfigureMapping = async (uploadId: string) => {
    try {
      // Fetch import detail to get file columns and mapping
      const response = await ImportManagementService.getImportDetail(uploadId);
      const importDetail: any = response?.data ?? null;

      if (!importDetail) {
        notify.error("Failed to Load Import", "Failed to load import details");
        return;
      }

      // Extract file columns from column_mapping
      const fileCols = Array.isArray(importDetail.column_mapping)
        ? importDetail.column_mapping
            .map((m: any) => m.file_column_name)
            .filter(Boolean)
        : [];

      setImportId(uploadId);
      setFileColumns(fileCols.length > 0 ? fileCols : []);
      setCurrentStep(2);

      // Set form values if mapping exists
      if (Array.isArray(importDetail.column_mapping)) {
        const formValues: any = {};
        importDetail.column_mapping.forEach((m: any) => {
          if (m.db_column_name && m.file_column_name) {
            formValues[m.db_column_name] = m.file_column_name;
          }
        });
        form.setFieldsValue(formValues);
        setIsMappingSaved(Object.keys(formValues).length > 0);
      }

      notify.success(
        "Import Loaded",
        "Import loaded. Configure column mapping."
      );
    } catch (error: any) {
      notify.error(
        "Failed to Load Import",
        error?.message ?? "Error loading import details"
      );
    }
  };

  // Handle delete import record
  const handleDelete = async (uploadId: string) => {
    try {
      await ImportManagementService.deleteImportRecord(uploadId);
      // Invalidate and refetch the import list
      queryClient.invalidateQueries({
        queryKey: ["imports", "list", SCHEMA_NAME, "city"],
      });
      // Also remove from local uploads if exists
      setUploads(prev => prev.filter(u => u.id !== uploadId));
      notify.success("Import Deleted", "Import record deleted successfully");
    } catch (error: any) {
      notify.error(
        "Failed to Delete Import",
        error?.message ?? "Error deleting import record"
      );
    }
  };

  // Convert API import list to uploads format
  const apiUploads: RosterUpload[] = useMemo(() => {
    const imports = importListData?.results?.data ?? [];
    if (imports.length === 0) {
      return [];
    }
    return imports.map((importRecord: any) => {
      // Map API status to component status
      let mappedStatus = "processing";
      if (
        importRecord.status === "Success" ||
        importRecord.status === "Partial Success"
      ) {
        mappedStatus = "completed";
      } else if (importRecord.status === "Failed") {
        mappedStatus = "failed";
      } else if (importRecord.status === "Processing") {
        mappedStatus = "processing";
      } else if (importRecord.status === "pending") {
        mappedStatus = "pending";
      }

      // Extract file name from file_name path
      const fileNameParts = importRecord.file_name?.split("/") ?? [];
      const fileName =
        fileNameParts[fileNameParts.length - 1] ??
        `import_${importRecord.id}.csv`;

      return {
        id: importRecord.id,
        file_name: fileName,
        status: mappedStatus,
        total_records: importRecord.total_rows ?? 0,
        new_users: importRecord.created_count ?? 0,
        updated_users: importRecord.updated_count ?? 0,
        deactivated_users: importRecord.failed_count ?? 0, // Use failed_count for deactivated
        errors:
          importRecord.failed_count && importRecord.failed_count > 0
            ? [{ count: importRecord.failed_count }]
            : [],
        created_at: importRecord.created_on ?? new Date().toISOString(),
        processed_at:
          mappedStatus === "completed" || mappedStatus === "failed"
            ? (importRecord.updated_on ?? new Date().toISOString())
            : null,
      };
    });
  }, [importListData]);

  // Use API uploads if available, otherwise use local uploads
  const displayUploads = apiUploads.length > 0 ? apiUploads : uploads;

  // Get stats from latest upload (from API or local) - use first item from API response
  const lastUpload = displayUploads?.[0];
  const stats = useMemo(() => {
    // If we have API data, use the first import record directly
    if (
      importListData?.results?.data &&
      importListData.results.data.length > 0
    ) {
      const firstImport = importListData.results.data[0];
      return {
        total: firstImport.total_rows ?? 0,
        new: firstImport.created_count ?? 0,
        updated: firstImport.updated_count ?? 0,
        deactivated: firstImport.failed_count ?? 0,
      };
    }
    // Fallback to lastUpload if no API data
    if (lastUpload) {
      return {
        total: lastUpload.total_records ?? 0,
        new: lastUpload.new_users ?? 0,
        updated: lastUpload.updated_users ?? 0,
        deactivated: lastUpload.deactivated_users ?? 0,
      };
    }
    return { total: 0, new: 0, updated: 0, deactivated: 0 };
  }, [importListData, lastUpload]);

  return (
    <div className="space-y-6">
      {/* Stats from last upload */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Total Records
              </span>
            </div>
            <div className="mt-1 text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-success h-4 w-4" />
              <span className="text-sm text-muted-foreground">New Users</span>
            </div>
            <div className="text-success mt-1 text-2xl font-bold">
              {stats.new}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-warning h-4 w-4" />
              <span className="text-sm text-muted-foreground">Updated</span>
            </div>
            <div className="text-warning mt-1 text-2xl font-bold">
              {stats.updated}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-muted-foreground">Deactivated</span>
            </div>
            <div className="mt-1 text-2xl font-bold text-destructive">
              {stats.deactivated}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Download Excel Format Button */}
      <div className="space-y-2">
        <div className="flex justify-end">
          <Button
            onClick={handleDownloadExcelFormat}
            disabled={isDownloading}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isDownloading ? "Downloading..." : "Download Excel Format"}
          </Button>
        </div>
        <p className="text-right text-xs text-muted-foreground">
          This format includes the column names. You can set data in the CSV or
          Excel file, then upload it. This format is applicable.
        </p>
      </div>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle>Step 1: Upload File</CardTitle>
          <CardDescription>
            Upload an Excel or CSV file for the MM Member schema.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <AntUpload.Dragger
            accept=".csv,.xlsx,.xls"
            beforeUpload={file => {
              handleFileUpload(file);
              return false;
            }}
            showUploadList={false}
            disabled={isUploading || currentStep > 1}
          >
            <div className="flex flex-col gap-4">
              {currentStep > 1 ? (
                <>
                  <CheckCircle className="text-success mx-auto" size={24} />
                  <p className="text-success font-medium">
                    File Upload Completed
                  </p>
                  <p className="text-sm text-muted-foreground">
                    File has been successfully uploaded and processed
                  </p>
                </>
              ) : (
                <>
                  <p>Click or drag file to this area to upload</p>
                  <p className="text-sm text-muted-foreground">
                    Support for CSV and Excel files (.csv, .xlsx, .xls)
                  </p>
                </>
              )}
            </div>
            {isUploading && (
              <div className="mt-4">
                <Progress
                  percent={75}
                  status="active"
                  strokeColor="hsl(var(--primary))"
                />
                <Text type="secondary" className="text-sm">
                  Processing file...
                </Text>
              </div>
            )}
          </AntUpload.Dragger>
        </CardContent>
      </Card>

      {/* Mapping */}
      {currentStep >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 2: Column Mapping</CardTitle>
            <CardDescription>
              Map your CSV columns to MM Member fields.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <Text strong>Column Mapping</Text>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetMapping}
                disabled={isUpdatingMapping || isRunningImport}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            </div>

            {isLoadingColumns ? (
              <div className="py-8 text-center">
                <Spin size="large" />
                <Text className="mt-4 block">Loading database schema...</Text>
              </div>
            ) : dbColumns.length === 0 ? (
              <Alert
                type="error"
                message="No database fields available for mapping"
                description="Please check your schema configuration."
                showIcon
              />
            ) : (
              <Form
                form={form}
                onFinish={handleColumnMapping}
                layout="vertical"
                className="space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {dbColumns.map(
                    (dbColumn: { label: string; value: string }) => (
                      <Card key={dbColumn.value}>
                        <CardContent className="pt-4">
                          <Form.Item
                            label={
                              <Space>
                                <Text strong>{dbColumn.label}</Text>
                                <Text type="secondary" className="text-xs">
                                  ({dbColumn.value})
                                </Text>
                              </Space>
                            }
                            name={dbColumn.value}
                          >
                            <Select
                              placeholder="Select file column"
                              allowClear
                              options={getMappableFileColumns().map(
                                fileColumn => {
                                  const currentValues = form.getFieldsValue();
                                  const isDisabled = Object.entries(
                                    currentValues
                                  ).some(
                                    ([key, val]) =>
                                      key !== dbColumn.value &&
                                      val === fileColumn
                                  );
                                  return {
                                    label: fileColumn,
                                    value: fileColumn,
                                    disabled: isDisabled,
                                  };
                                }
                              )}
                              showSearch
                              filterOption={filterByLabel}
                            />
                          </Form.Item>
                        </CardContent>
                      </Card>
                    )
                  )}
                </div>

                {mappingError && (
                  <Alert
                    type="error"
                    message={mappingError}
                    showIcon
                    className="mb-4"
                  />
                )}

                {fileColumns.length > getMappableFileColumns().length && (
                  <Alert
                    type="info"
                    message="System fields are automatically handled"
                    description="System fields (id, is_active, created_on, etc.) are automatically handled during import and don't need to be mapped."
                    showIcon
                    className="mb-4"
                  />
                )}

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={async () => {
                      const values = form.getFieldsValue();
                      const hasValues = Object.values(values).some(
                        val => val && val !== ""
                      );

                      if (!hasValues) {
                        notify.warning(
                          "No Mappings",
                          "Please map at least one column before proceeding."
                        );
                        return;
                      }

                      await handleColumnMapping(values);
                    }}
                    disabled={isUpdatingMapping || isRunningImport}
                  >
                    {isUpdatingMapping ? "Updating..." : "Update Mapping"}
                  </Button>

                  {isMappingSaved && (
                    <Button
                      type="button"
                      onClick={handleRunImport}
                      disabled={isUpdatingMapping || isRunningImport}
                      className="bg-green-600 text-white hover:bg-green-700"
                    >
                      <Play className="mr-2 h-4 w-4" />
                      {isRunningImport ? "Importing..." : "Start Import"}
                    </Button>
                  )}
                </div>
              </Form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload History */}
      <Card>
        <CardHeader>
          <CardTitle>Upload History</CardTitle>
          <CardDescription>
            Recent roster uploads and their processing status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!displayUploads?.length ? (
            <div className="py-8 text-center text-muted-foreground">
              No uploads yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Records</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayUploads.map(upload => (
                  <TableRow key={upload.id}>
                    <TableCell className="font-medium">
                      {upload.file_name}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={statusColors[upload.status]}
                        className="flex w-fit items-center gap-1"
                      >
                        {statusIcons[upload.status]}
                        {upload.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{upload.new_users}</TableCell>
                    <TableCell>
                      {format(new Date(upload.created_at), "MMM d, HH:mm")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {upload.status === "pending" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleConfigureMapping(upload.id)}
                            className="h-8"
                            title="Configure Column Mapping"
                          >
                            <Settings className="mr-1 h-3 w-3" />
                            Configure Mapping
                          </Button>
                        )}

                        <Popconfirm
                          title="Delete Import Record"
                          description="Are you sure you want to delete this import record? This action cannot be undone."
                          onConfirm={() => handleDelete(upload.id)}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            title="Delete Import Record"
                          >
                            <Trash className="h-3 w-3 text-destructive" />
                          </Button>
                        </Popconfirm>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ImportPreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        members={previewMembers}
        fileName={uploadedFile?.name ?? "import_file"}
        onConfirmImport={async updatedMembers => {
          // Only members that have been assigned a status
          const assigned = updatedMembers.filter(
            m => m.status === "retired" || m.status === "job_changed"
          );

          if (assigned.length === 0) {
            notify.error(
              "No Status Assigned",
              "Please assign Retired or Change status to at least one member."
            );
            return;
          }

          try {
            notify.info(
              "Updating Members",
              `Updating status for ${assigned.length} member(s)...`
            );

            // Call API for each assigned member
            const results = await Promise.allSettled(
              assigned.map(m =>
                CityCollaborationService.updateMemberStatus(
                  m.id as string,
                  m.status === "retired" ? "Retired" : "Resigned"
                )
              )
            );

            const failedCount = results.filter(
              r => r.status === "rejected"
            ).length;

            if (failedCount > 0) {
              notify.error(
                "Partial Update",
                `${failedCount} member(s) could not be updated. Please try again.`
              );
            } else {
              notify.success(
                "Status Updated",
                `${assigned.length} member(s) updated successfully.`
              );
            }

            // Close dialog and re-run the import
            setIsPreviewOpen(false);
            handleRunImport();
          } catch (error: any) {
            notify.error(
              "Update Failed",
              error?.message ?? "Failed to update member statuses."
            );
          }
        }}
      />
    </div>
  );
}
